//***********************************************************
// Angular
//***********************************************************
import { NgModule, APP_INITIALIZER } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { FormsModule } from '@angular/forms';
import { ReactiveFormsModule } from '@angular/forms';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';

//***********************************************************
// MJ - Consolidated Module Bundles
//***********************************************************
import {
  MJExplorerModulesBundle,
  SharedService
} from '@memberjunction/ng-explorer-modules';
import { AuthServicesModule, RedirectComponent, MJAuthBase } from '@memberjunction/ng-auth-services';
import { MJExplorerAppModule } from '@memberjunction/ng-explorer-app';

// Lazy loading infrastructure
import { LazyModuleRegistry, LAZY_FEATURE_CONFIG , LazyFeatureChunk } from '@memberjunction/ng-explorer-core';
import { ProviderBase } from '@memberjunction/core';

// Import lite class registrations manifest (excludes lazy-loaded dashboard and settings packages)
import {CLASS_REGISTRATIONS} from '@memberjunction/ng-bootstrap-lite';

// Import supplemental manifest for user-defined classes (generated at prestart with --exclude-packages @memberjunction)
import {CLASS_REGISTRATIONS as LOCAL_CLASSES} from './generated/class-registrations-manifest';

// SoundPost custom overrides (eager — must register before shell initializes)
import { SoundPostUserMenu } from './shared/services/soundpost-user-menu';

// static code path builder
const combinedClasses = [...CLASS_REGISTRATIONS, ...LOCAL_CLASSES, SoundPostUserMenu];

// SoundPost dashboard lazy-loading configuration
// MJ 6.x merges every RunView issued within a 10 ms window into ONE server batch and rejects ALL
// callers if ANY query in the batch fails (ProviderBase.flushCoalesceQueue). On 6.1.5 MJ's own
// Employees/Companies overview form panels query the unprefixed entity names ('Employees') and fail,
// which took the SoundPost Posting dashboard down with them (console: "Entity Employees not found in
// metadata"). Fixed upstream on next (MJ 13739a70ea, 2026-10-05) but not on the 6.1 line yet.
// NOTE: coalescing itself is NOT new in 6.x (identical code and 10 ms default in 5.44, and it ran in
// prod); what is new is the broken panels that can land in the same window. 0 = run each RunView
// independently (dedup + 5 s linger still apply). Cost: parallel bursts on a dashboard become N
// concurrent requests instead of one. Revisit when a 6.1 patch carries the panel fix.
ProviderBase.CoalesceWindowMs = 0;

const LOCAL_LAZY_CONFIG: Record<string, LazyFeatureChunk> = {
  'BaseResourceComponent::AccountActivityResource':
    { chunkId: './dashboards/account-activity/account-activity.module', load: () => import('./dashboards/account-activity/account-activity.module').then(() => {}) },
  'BaseResourceComponent::AccountingEntriesResource':
    { chunkId: './dashboards/accounting-entries/accounting-entries.module', load: () => import('./dashboards/accounting-entries/accounting-entries.module').then(() => {}) },
  'BaseResourceComponent::ProductGLActivityResource':
    { chunkId: './dashboards/product-gl-activity/product-gl-activity.module', load: () => import('./dashboards/product-gl-activity/product-gl-activity.module').then(() => {}) },
  'BaseResourceComponent::AgreementGLActivityResource':
    { chunkId: './dashboards/agreement-gl-activity/agreement-gl-activity.module', load: () => import('./dashboards/agreement-gl-activity/agreement-gl-activity.module').then(() => {}) },
  'BaseResourceComponent::OrderMetricsResource':
    { chunkId: './dashboards/order-metrics/order-metrics.module', load: () => import('./dashboards/order-metrics/order-metrics.module').then(() => {}) },
  'BaseResourceComponent::PaymentsResource':
    { chunkId: './dashboards/payments/payments.module', load: () => import('./dashboards/payments/payments.module').then(() => {}) },
  'BaseResourceComponent::EntrySearchResource':
    { chunkId: './dashboards/entry-search/entry-search.module', load: () => import('./dashboards/entry-search/entry-search.module').then(() => {}) },
  'BaseResourceComponent::ARCollectionsResource':
    { chunkId: './dashboards/ar-collections/ar-collections.module', load: () => import('./dashboards/ar-collections/ar-collections.module').then(() => {}) },
  'BaseResourceComponent::DeferredRevenueResource':
    { chunkId: './dashboards/deferred-revenue/deferred-revenue.module', load: () => import('./dashboards/deferred-revenue/deferred-revenue.module').then(() => {}) },
  'BaseResourceComponent::GLAccountsResource':
    { chunkId: './dashboards/gl-accounts/gl-accounts.module', load: () => import('./dashboards/gl-accounts/gl-accounts.module').then(() => {}) },
  'BaseResourceComponent::ProductManagementResource':
    { chunkId: './dashboards/product-management/product-management.module', load: () => import('./dashboards/product-management/product-management.module').then(() => {}) },
  'BaseResourceComponent::UnpostedEntriesResource':
    { chunkId: './dashboards/unposted-entries/unposted-entries.module', load: () => import('./dashboards/unposted-entries/unposted-entries.module').then(() => {}) },
  'BaseResourceComponent::AccountingPeriodsResource':
    { chunkId: './dashboards/accounting-periods/accounting-periods.module', load: () => import('./dashboards/accounting-periods/accounting-periods.module').then(() => {}) },
  'BaseResourceComponent::AllocationRulesResource':
    { chunkId: './dashboards/allocation-rules/allocation-rules.module', load: () => import('./dashboards/allocation-rules/allocation-rules.module').then(() => {}) },
  'BaseResourceComponent::ConfigKeysResource':
    { chunkId: './dashboards/config-keys/config-keys.module', load: () => import('./dashboards/config-keys/config-keys.module').then(() => {}) },
  'BaseResourceComponent::DimensionsResource':
    { chunkId: './dashboards/dimensions/dimensions.module', load: () => import('./dashboards/dimensions/dimensions.module').then(() => {}) },
  'BaseResourceComponent::GLMappingRulesResource':
    { chunkId: './dashboards/gl-mapping-rules/gl-mapping-rules.module', load: () => import('./dashboards/gl-mapping-rules/gl-mapping-rules.module').then(() => {}) },
  'BaseResourceComponent::PostingResource':
    { chunkId: './dashboards/posting/posting.module', load: () => import('./dashboards/posting/posting.module').then(() => {}) },
  'BaseResourceComponent::ProductAccountingResource':
    { chunkId: './dashboards/product-accounting/product-accounting.module', load: () => import('./dashboards/product-accounting/product-accounting.module').then(() => {}) },
  'BaseResourceComponent::SponsorshipManagementResource':
    { chunkId: './dashboards/sponsorship-management/sponsorship-management.module', load: () => import('./dashboards/sponsorship-management/sponsorship-management.module').then(() => {}) },
  'BaseResourceComponent::AccountingEditorResource':
    { chunkId: './dashboards/accounting-editor/accounting-editor.module', load: () => import('./dashboards/accounting-editor/accounting-editor.module').then(() => {}) },
  'BaseResourceComponent::IntegrationOperationsResource':
    { chunkId: './dashboards/integration-operations/integration-operations.module', load: () => import('./dashboards/integration-operations/integration-operations.module').then(() => {}) },
};

//***********************************************************
//MSAL
//***********************************************************
import { MsalGuardConfiguration } from '@azure/msal-angular';
import { InteractionType } from '@azure/msal-browser';

//***********************************************************
// Project stuff
//***********************************************************
import { AppComponent } from './app.component';
import { GeneratedFormsModule } from './generated/generated-forms.module';
import { environment } from '../environments/environment';

/**
 * Set your default interaction type for MSALGuard here. If you have any
 * additional scopes you want the user to consent upon login, add them here as well.
 */
export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
  };
}

/**
 * Initialize auth provider before Angular routing starts
 * This ensures MSAL can process OAuth redirect responses before Angular's router
 * consumes the URL hash
 */
export function initializeAuth(authService: MJAuthBase): () => Promise<void> {
  return () => authService.initialize();
}

@NgModule({
  declarations: [
    AppComponent, 
  ],
  imports: [
    // Angular Core Modules
    BrowserModule,
    BrowserAnimationsModule,
    FormsModule,
    ReactiveFormsModule,

    // MJ Consolidated Bundle (includes all MJ + Kendo modules)
    MJExplorerModulesBundle,

    // Auth (needs forRoot configuration)
    AuthServicesModule.forRoot(environment),

    // Explorer App Shell (includes login UI, validation, and mj-shell wrapper)
    MJExplorerAppModule.forRoot(environment),

    // App-specific modules
    GeneratedFormsModule
  ],
  providers: [
    SharedService,
    provideHttpClient(withInterceptorsFromDi()),
    {
      provide: APP_INITIALIZER,
      useFactory: initializeAuth,
      deps: [MJAuthBase],
      multi: true
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (lazyRegistry: LazyModuleRegistry) => async () => {
        lazyRegistry.RegisterBulk(LAZY_FEATURE_CONFIG);
        lazyRegistry.RegisterBulk(LOCAL_LAZY_CONFIG);
        lazyRegistry.WireToClassFactory();
        // Eagerly load SoundPost dashboard modules to guarantee ClassFactory registration.
        // MJ's own LAZY_FEATURE_CONFIG entries import from npm packages which the Vite dev
        // server resolves differently than local relative-path chunks. Eager import here
        // ensures the module-level ClassFactory.Register() call fires before any tab loads.
        await Promise.all([
          import('./dashboards/account-activity/account-activity.module'),
          import('./dashboards/accounting-entries/accounting-entries.module'),
          import('./dashboards/product-gl-activity/product-gl-activity.module'),
          import('./dashboards/agreement-gl-activity/agreement-gl-activity.module'),
          import('./dashboards/order-metrics/order-metrics.module'),
          import('./dashboards/payments/payments.module'),
          import('./dashboards/entry-search/entry-search.module'),
          import('./dashboards/ar-collections/ar-collections.module'),
          import('./dashboards/deferred-revenue/deferred-revenue.module'),
          import('./dashboards/gl-accounts/gl-accounts.module'),
          import('./dashboards/product-management/product-management.module'),
          import('./dashboards/unposted-entries/unposted-entries.module'),
          import('./dashboards/accounting-periods/accounting-periods.module'),
          import('./dashboards/allocation-rules/allocation-rules.module'),
          import('./dashboards/config-keys/config-keys.module'),
          import('./dashboards/dimensions/dimensions.module'),
          import('./dashboards/gl-mapping-rules/gl-mapping-rules.module'),
          import('./dashboards/posting/posting.module'),
          import('./dashboards/product-accounting/product-accounting.module'),
          import('./dashboards/sponsorship-management/sponsorship-management.module'),
          import('./dashboards/accounting-editor/accounting-editor.module'),
          import('./dashboards/integration-operations/integration-operations.module'),
        ]);
      },
      deps: [LazyModuleRegistry],
      multi: true
    }
  ],
  bootstrap: [AppComponent, RedirectComponent],
})
export class AppModule {}
